import React, { useEffect, useRef, useState } from "react";
import Multiselect from "multiselect-react-dropdown";
import { useSelector } from "react-redux";

import Constants from "../../constants";
import { RootState } from "../../globals";
import HttpManager from "../../services/HttpManager";
import { AdvisorCandidateDTO, AdvisorSelection } from "../../models/EnrollmentHistoryDTO";

export interface AdvisorsPickerProps {
	campusId: number;
	value: AdvisorSelection[] | null;
	onChange: (advisors: AdvisorSelection[] | null) => void;
	disabled?: boolean;
	invalid?: boolean;
	/** false = no se ofrece "Ninguno": en el alta de matrícula siempre hubo alguien que atendió (Esteban, 2026-09-22). */
	allowNone?: boolean;
	/**
	 * Sin nada elegido (value === null), si quien está en sesión es asesora del plantel queda ella sola al 100 % (Esteban, 2026-10-31:
	 * «asesora de ventas casi siempre es 1 nada más»). Solo la primera vez que llega la lista; si la quita, no vuelve a ponerse.
	 */
	defaultSelf?: boolean;
}

/**
 * Picks the people credited with a sale (asesoras).
 * Candidates come from the API: enabled users holding a sales/admissions role in the campus.
 * The field is mandatory, so it has three states (Esteban, 2026-09-11):
 * null = nothing chosen yet (blocks the save), [] = "Ninguno" chosen on purpose, [...] = advisors.
 */
/**
 * La candidata soy yo: el id de la rueda es el de Zoho (o "wapp:<id>" para quien no tiene Zoho), así que se compara por "wapp:<mi id>"
 * o por correo (misma regla que el servidor en SolicitudPromoService::yoComoAsesora: correo exacto o, si es único, lo de antes del @,
 * porque Zoho usa .com y varias cuentas de WAPP usan .mx).
 */
const yoEntre = (candidates: AdvisorCandidateDTO[], yoId: number, yoEmail: string): AdvisorCandidateDTO | null => {
	const correo = (c: AdvisorCandidateDTO) => String(c.email || "").trim().toLowerCase();
	const exacta = candidates.find((c) => String(c.id || "") === "wapp:" + yoId || String(c.id || "") === String(yoId) || (!!yoEmail && correo(c) === yoEmail));
	if (exacta) return exacta;
	if (!yoEmail) return null;
	const local = yoEmail.split("@")[0];
	const porLocal = candidates.filter((c) => !!correo(c) && correo(c).split("@")[0] === local);
	return porLocal.length === 1 ? porLocal[0] : null;
};

const AdvisorsPicker: React.FC<AdvisorsPickerProps> = ({ campusId, value, onChange, disabled, invalid, allowNone = true, defaultSelf = false }) => {
	const http = HttpManager.getInstance();
	const [candidates, setCandidates] = useState<AdvisorCandidateDTO[]>([]);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [reload, setReload] = useState(0);
	const yoId: number | null = useSelector((state: RootState) => (state.rootReducer.sessionState as any).user?.id ?? null);
	const yoEmail: string = useSelector((state: RootState) => String((state.rootReducer.sessionState as any).user?.email || "").trim().toLowerCase());
	const selfPuesta = useRef<boolean>(false);

	// Yo al 100 % por omisión: una sola vez, cuando llega la lista y todavía no hay nada elegido.
	useEffect(() => {
		if (!defaultSelf || selfPuesta.current || value !== null || candidates.length < 1 || yoId === null) return;
		selfPuesta.current = true;
		const yo = yoEntre(candidates, yoId, yoEmail);
		if (yo) onChange([{ userId: yo.id, name: yo.name, sharePercentage: 100 }]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultSelf, candidates, yoId]);

	//A dropped connection used to leave the list empty for good (the request never reached the server,
	//Esteban 2026-09-26): retry on our own a few times, then offer "Reintentar".
	useEffect(() => {
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const esperas = [0, 1500, 4000, 8000];
		const intentar = (n: number) => {
			http.enrollmentService.getAdvisorCandidates(campusId)
				.then((response) => {
					if (cancelled) { return; }
					setCandidates(Array.isArray(response.data) ? response.data : []);
					setLoadError(null);
				})
				.catch((error) => {
					if (cancelled) { return; }
					console.error("Asesoras: no se pudo cargar la lista (intento " + (n + 1) + ")", error);
					if (n + 1 < esperas.length) {
						timer = setTimeout(() => intentar(n + 1), esperas[n + 1]);
					} else {
						setLoadError(Constants.Common.advisorsLoadError);
					}
				});
		};
		setLoadError(null);
		intentar(0);
		return () => { cancelled = true; if (timer) { clearTimeout(timer); } };
	}, [campusId, reload]);

	const NONE_ID = "__none__";
	const noneOption: AdvisorCandidateDTO = { id: NONE_ID, name: Constants.Common.noneOption, email: null, roles: [] };
	const options = allowNone ? [noneOption, ...candidates] : candidates;
	// "Ninguno" se muestra como una selección más, para que se vea que fue una decisión.
	const selectedCandidates = value === null
		? []
		: (value.length < 1 ? (allowNone ? [noneOption] : []) : candidates.filter((candidate) => value.some((advisor) => advisor.userId === candidate.id)));

	const handleSelect = (selected: AdvisorCandidateDTO[]) => {
		const conNinguno = selected.some((candidate) => candidate.id === NONE_ID);
		const otras = selected.filter((candidate) => candidate.id !== NONE_ID);
		//Si ya estaba en "Ninguno" y ahora eligen a alguien, gana la asesora; si tenían
		//asesoras y eligen "Ninguno", gana "Ninguno".
		if (conNinguno && !(value !== null && value.length < 1 && otras.length > 0)) {
			onChange([]);
			return;
		}
		if (otras.length < 1) {
			//Quitar todo deja el campo sin responder: vuelve a quedar pendiente.
			onChange(null);
			return;
		}
		onChange(otras.map((candidate) => {
			const existing = (value ?? []).find((advisor) => advisor.userId === candidate.id);
			return existing ?? { userId: candidate.id, name: candidate.name, sharePercentage: null };
		}));
	};

	const pendiente = value === null;

	return (
		<div className="advisorsPicker">
			<label style={{ position: "relative", display: "block" }}>
				{Constants.Common.advisors} <span style={{ color: "var(--wa-rojo)" }}>*</span>
			</label>
			{loadError
				? <div className="text-danger" style={{ fontSize: 13 }}>
					{loadError}{" "}
					<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: 13, verticalAlign: "baseline" }}
						onClick={() => setReload((n) => n + 1)}>Reintentar</button>
				</div>
				: ""}
			<div style={invalid && pendiente ? { border: "1px solid var(--wa-rojo)", borderRadius: 6 } : undefined}>
				<Multiselect
					options={options}
					displayValue="name"
					selectedValues={selectedCandidates}
					onSelect={handleSelect}
					onRemove={handleSelect}
					disable={disabled}
					placeholder={selectedCandidates.length < 1 ? Constants.Common.selectAdvisors : ""}
					emptyRecordMsg={Constants.Common.noMoreOptions}
					avoidHighlightFirstOption
				/>
			</div>
			<div style={{ fontSize: 13, color: pendiente ? "var(--wa-rojo)" : "var(--wa-apagado)", marginTop: 4 }}>
				{pendiente
					? (allowNone ? Constants.Common.advisorsRequired : "Elija a la asesora o asesoras que atendieron al alumno.")
					: (value.length < 1
						? (allowNone ? Constants.Common.noneOption + " · " + Constants.Common.noAdvisorsHint : "Elija a la asesora o asesoras que atendieron al alumno.")
						: Constants.Common.advisorsSelectedHint)}
			</div>
		</div>
	);
};

export default AdvisorsPicker;
